import { useState, type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';

type DashboardPeriod = 'today' | 'yesterday' | '7d' | '30d';

interface MetricPair {
  current: number;
  previous: number;
}

interface DashboardSummary {
  period: DashboardPeriod;
  range: {
    currentStart: string;
    currentEnd: string;
    previousStart: string;
    previousEnd: string;
  };
  metrics: {
    mensagens_cliente: MetricPair;
    mensagens_sara: MetricPair;
    conversas_ativas: MetricPair;
    novos_clientes: MetricPair;
    tiragens_feitas: MetricPair;
    custo_llm_usd: MetricPair;
  };
  snapshots: {
    clientes_total_ativas: number;
    tickets_abertos_total: number;
    tickets_por_tipo: Record<string, number>;
    webhooks_failed: number;
    conversas_pausadas: number;
    conversas_com_risco: number;
  };
}

const PERIODS: Array<{ value: DashboardPeriod; label: string }> = [
  { value: 'today', label: 'Hoje' },
  { value: 'yesterday', label: 'Ontem' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
];

const COMPARISON_LABEL: Record<DashboardPeriod, string> = {
  today: 'vs ontem (mesmo horário)',
  yesterday: 'vs anteontem',
  '7d': 'vs 7 dias anteriores',
  '30d': 'vs 30 dias anteriores',
};

const TIPO_LABEL: Record<string, string> = {
  reembolso: 'Reembolso',
  risco_emocional: 'Risco emocional',
  cliente_agressiva: 'Cliente agressiva',
  fora_escopo: 'Fora de escopo',
  intervencao_solicitada: 'Intervenção solicitada',
  bonus_nao_recebido: 'Bônus não recebido',
  dados_mapa_invalidos: 'Dados mapa inválidos',
  upsell_pendente: 'Oportunidade upsell',
  verificacao_pix_manual: 'Verificação Pix manual',
  mapa_astral_pendente: 'Mapa astral pendente',
  queixa_repeticao: 'Queixa de repetição',
  tiragem_loop_travado: 'Tiragem travada em loop',
  compra_nao_localizada: 'Compra não localizada',
  outro: 'Outro',
};

const TIPO_COLOR: Record<string, string> = {
  reembolso: 'text-sara-warning',
  risco_emocional: 'text-sara-danger',
  cliente_agressiva: 'text-sara-danger',
  upsell_pendente: 'text-sara-success',
  mapa_astral_pendente: 'text-sara-accent',
  verificacao_pix_manual: 'text-sara-warning',
  // Identidade partida: a cliente já pagou e está sendo cobrada de novo.
  compra_nao_localizada: 'text-sara-danger',
};

function formatNumber(n: number): string {
  return n.toLocaleString('pt-BR');
}

function formatUsd(n: number): string {
  return `US$ ${n.toFixed(2)}`;
}

function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null;
  return ((current - previous) / previous) * 100;
}

function formatVariation(
  current: number,
  previous: number,
  hint: string,
): { text: string; tone: 'up' | 'down' | 'flat' | 'unknown' } {
  const pct = pctChange(current, previous);
  if (pct === null) {
    return { text: previous === 0 ? `0 → ${formatNumber(current)} ${hint}` : '—', tone: 'unknown' };
  }
  if (pct === 0) return { text: `0% ${hint}`, tone: 'flat' };
  const arrow = pct > 0 ? '▲' : '▼';
  const tone = pct > 0 ? 'up' : 'down';
  const sign = pct > 0 ? '+' : '';
  return { text: `${arrow} ${sign}${pct.toFixed(1)}% ${hint}`, tone };
}

function MetricCard({
  label,
  current,
  previous,
  format = formatNumber,
  hint,
  // Quando true, "subir" é coisa ruim (custo). Inverte cor.
  invertTone = false,
}: {
  label: string;
  current: number;
  previous: number;
  format?: (n: number) => string;
  hint: string;
  invertTone?: boolean;
}): JSX.Element {
  const v = formatVariation(current, previous, hint);
  const toneColor =
    v.tone === 'unknown'
      ? 'text-sara-text-muted'
      : v.tone === 'flat'
        ? 'text-sara-text-muted'
        : v.tone === 'up'
          ? invertTone
            ? 'text-sara-warning'
            : 'text-sara-success'
          : invertTone
            ? 'text-sara-success'
            : 'text-sara-warning';
  return (
    <div className="card-hover">
      <dt className="text-sm text-sara-text-muted">{label}</dt>
      <dd className="mt-1 text-3xl font-semibold tracking-tight text-sara-text">
        {format(current)}
      </dd>
      <p className={`mt-2 text-xs ${toneColor}`} aria-label={`variação ${v.text}`}>
        {v.text}
      </p>
      <p className="mt-1 text-[11px] text-sara-text/50">antes: {format(previous)}</p>
    </div>
  );
}

function SnapshotCard({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  tone?: 'neutral' | 'warning' | 'danger' | 'success';
}): JSX.Element {
  const valueClass =
    tone === 'danger'
      ? 'text-sara-danger'
      : tone === 'warning'
        ? 'text-sara-warning'
        : tone === 'success'
          ? 'text-sara-success'
          : 'text-sara-text';
  return (
    <div className="card-hover">
      <dt className="text-sm text-sara-text-muted">{label}</dt>
      <dd className={`mt-1 text-3xl font-semibold tracking-tight ${valueClass}`}>{value}</dd>
    </div>
  );
}

export function DashboardPage(): JSX.Element {
  const [period, setPeriod] = useState<DashboardPeriod>('today');

  const { data, isLoading, isError, refetch } = useQuery<DashboardSummary>({
    queryKey: ['dashboard-summary', period],
    queryFn: () => request<DashboardSummary>(`/admin/dashboard/summary?period=${period}`),
    refetchInterval: 30_000,
  });

  // Avisos de "precisa de atenção" — reusa os endpoints já testados das telas
  // /mapas e /duplicatas. Poll mais lento (60s) pra não pesar (o de duplicatas
  // é um scan). Fetch derivado em contagem.
  const { data: mapasPendentes } = useQuery<number>({
    queryKey: ['dashboard-mapas-pendentes'],
    queryFn: async () => {
      const items = await request<Array<{ bucket: string }>>('/admin/mapas/inbox');
      return items.filter((m) => m.bucket === 'precisa_de_voce').length;
    },
    refetchInterval: 60_000,
  });
  const { data: duplicatasPendentes } = useQuery<number>({
    queryKey: ['dashboard-duplicatas-pendentes'],
    queryFn: async () => {
      const grupos = await request<unknown[]>('/admin/clientes/duplicados');
      return grupos.length;
    },
    refetchInterval: 60_000,
  });

  const mapasN = mapasPendentes ?? 0;
  const dupN = duplicatasPendentes ?? 0;

  const compareHint = COMPARISON_LABEL[period];

  return (
    <section aria-labelledby="dashboard-heading" className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 id="dashboard-heading" className="h1">
            Visão geral
          </h1>
          <p className="muted text-sm mt-1">
            Indicadores em tempo real do atendimento da Sara. Atualiza a cada 30s.
          </p>
        </div>
        <fieldset className="flex flex-wrap gap-1" role="radiogroup" aria-label="Período">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={period === p.value}
              onClick={() => setPeriod(p.value)}
              className={`btn-ghost text-xs ${period === p.value ? 'bg-sara-accent/20 border-sara-accent' : ''}`}
            >
              {p.label}
            </button>
          ))}
        </fieldset>
      </header>

      {(mapasN > 0 || dupN > 0) && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          role="region"
          aria-label="Precisa de atenção"
        >
          {mapasN > 0 && (
            <a
              href="/mapas"
              className="card-hover flex items-center justify-between rounded-md border-l-4 border-sara-warning focus:outline-none focus:ring-2 focus:ring-sara-accent"
            >
              <span className="text-sm">
                🗺️ Mapas astrais aguardando você
                <span className="block text-xs text-sara-text-muted">
                  aprovação / erro / entrega
                </span>
              </span>
              <span className="text-3xl font-semibold text-sara-warning">
                {formatNumber(mapasN)}
              </span>
            </a>
          )}
          {dupN > 0 && (
            <a
              href="/duplicatas"
              className="card-hover flex items-center justify-between rounded-md border-l-4 border-sara-danger focus:outline-none focus:ring-2 focus:ring-sara-accent"
            >
              <span className="text-sm">
                👥 Duplicatas pra revisar
                <span className="block text-xs text-sara-text-muted">
                  cadastros possivelmente da mesma pessoa
                </span>
              </span>
              <span className="text-3xl font-semibold text-sara-danger">{formatNumber(dupN)}</span>
            </a>
          )}
        </div>
      )}

      {isLoading && (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          role="status"
          aria-live="polite"
        >
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-4 bg-sara-surface-hover rounded w-24 mb-2" />
              <div className="h-8 bg-sara-surface-hover rounded w-16" />
              <div className="h-3 bg-sara-surface-hover rounded w-20 mt-3" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div role="alert" className="card border-sara-danger">
          <p className="text-sara-danger mb-2">Não consegui carregar os indicadores.</p>
          <button type="button" className="btn-ghost" onClick={() => void refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-8 animate-fade-in">
          <div>
            <h2 className="text-base font-semibold mb-3 text-sara-text-muted">
              Atividade no período
            </h2>
            <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <MetricCard
                label="Mensagens da cliente"
                current={data.metrics.mensagens_cliente.current}
                previous={data.metrics.mensagens_cliente.previous}
                hint={compareHint}
              />
              <MetricCard
                label="Mensagens da Sara"
                current={data.metrics.mensagens_sara.current}
                previous={data.metrics.mensagens_sara.previous}
                hint={compareHint}
              />
              <MetricCard
                label="Conversas ativas"
                current={data.metrics.conversas_ativas.current}
                previous={data.metrics.conversas_ativas.previous}
                hint={compareHint}
              />
              <MetricCard
                label="Novos clientes"
                current={data.metrics.novos_clientes.current}
                previous={data.metrics.novos_clientes.previous}
                hint={compareHint}
              />
              <MetricCard
                label="Tiragens feitas"
                current={data.metrics.tiragens_feitas.current}
                previous={data.metrics.tiragens_feitas.previous}
                hint={compareHint}
              />
              <MetricCard
                label="Custo LLM"
                current={data.metrics.custo_llm_usd.current}
                previous={data.metrics.custo_llm_usd.previous}
                format={formatUsd}
                hint={compareHint}
                invertTone
              />
            </dl>
          </div>

          <div>
            <h2 className="text-base font-semibold mb-3 text-sara-text-muted">
              Tickets abertos agora
            </h2>
            {data.snapshots.tickets_abertos_total === 0 ? (
              <div className="card text-sm text-sara-text-muted">Nenhum ticket aberto. ✨</div>
            ) : (
              <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {Object.entries(data.snapshots.tickets_por_tipo)
                  .sort((a, b) => b[1] - a[1])
                  .map(([tipo, count]) => (
                    <a
                      key={tipo}
                      href={`/tickets?status=aberto&tipo=${tipo}`}
                      className="card-hover block focus:outline-none focus:ring-2 focus:ring-sara-accent rounded-md"
                    >
                      <dt className="text-xs text-sara-text-muted">{TIPO_LABEL[tipo] ?? tipo}</dt>
                      <dd
                        className={`mt-1 text-2xl font-semibold ${TIPO_COLOR[tipo] ?? 'text-sara-text'}`}
                      >
                        {formatNumber(count)}
                      </dd>
                    </a>
                  ))}
              </dl>
            )}
            <p className="text-xs text-sara-text-muted mt-2">
              Total: {formatNumber(data.snapshots.tickets_abertos_total)} ticket
              {data.snapshots.tickets_abertos_total === 1 ? '' : 's'} aberto
              {data.snapshots.tickets_abertos_total === 1 ? '' : 's'}.
            </p>
          </div>

          <div>
            <h2 className="text-base font-semibold mb-3 text-sara-text-muted">
              Saúde do atendimento
            </h2>
            <dl className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <SnapshotCard
                label="Clientes ativas"
                value={formatNumber(data.snapshots.clientes_total_ativas)}
              />
              <SnapshotCard
                label="Conversas pausadas"
                value={formatNumber(data.snapshots.conversas_pausadas)}
                tone={data.snapshots.conversas_pausadas > 0 ? 'warning' : 'neutral'}
              />
              <SnapshotCard
                label="Risco ativo"
                value={formatNumber(data.snapshots.conversas_com_risco)}
                tone={data.snapshots.conversas_com_risco > 0 ? 'danger' : 'neutral'}
              />
              <SnapshotCard
                label="Webhooks com erro"
                value={formatNumber(data.snapshots.webhooks_failed)}
                tone={data.snapshots.webhooks_failed > 0 ? 'warning' : 'success'}
              />
              <SnapshotCard
                label="Tickets abertos"
                value={formatNumber(data.snapshots.tickets_abertos_total)}
                tone={data.snapshots.tickets_abertos_total > 0 ? 'warning' : 'success'}
              />
            </dl>
          </div>
        </div>
      )}
    </section>
  );
}
